// src/components/Admin/StatsCards.js
import { useState, useEffect } from "react";
import {
  Users,
  Briefcase,
  DollarSign,
  Clock,
  AlertTriangle,
} from "lucide-react";
import { fetchDashboardStats } from "../services/fetchDashboardStats";

export default function StatsCards() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await fetchDashboardStats();
        if (alive) setStats(s);
      } catch (err) {
        console.error("fetchDashboardStats error:", err);
        if (alive) setError("Could not load stats.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (loading)
    return <div className="text-gray-500 py-8">Loading stats...</div>;
  if (error) return <div className="text-red-600 py-8">{error}</div>;

  return (
    <div className="grid md:grid-cols-4 gap-6 mb-6">
      {/* Total Users */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <Users className="text-[#006C35]" size={24} />
          <span className="text-gray-600">Total Users</span>
        </div>
        <div className="text-3xl text-[#1E3A8A]">{stats.totalUsers}</div>
        <div className="text-sm text-gray-500 mt-2">
          {stats.customers} customers, {stats.taskers} providers
        </div>
        {stats.suspended > 0 && (
          <div className="text-xs text-red-600 mt-2 flex items-center gap-1">
            <AlertTriangle size={12} />
            {stats.suspended} suspended
          </div>
        )}
      </div>

      {/* Jobs */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <Briefcase className="text-[#006C35]" size={24} />
          <span className="text-gray-600">Jobs</span>
        </div>
        <div className="text-3xl text-[#1E3A8A]">{stats.activeJobs}</div>
        <div className="text-sm text-gray-500 mt-2">
          {stats.completedJobs} completed of {stats.totalJobs}
        </div>
        {stats.noTaskerJobs > 0 && (
          <div className="text-xs text-orange-600 mt-2 flex items-center gap-1">
            <Clock size={12} />
            {stats.noTaskerJobs} unmatched
          </div>
        )}
      </div>

      {/* Completed */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <Briefcase className="text-[#006C35]" size={24} />
          <span className="text-gray-600">Completed Jobs</span>
        </div>
        <div className="text-3xl text-[#1E3A8A]">{stats.completedJobs}</div>
        <div className="text-sm text-gray-500 mt-2">
          {stats.cancelledJobs} cancelled
        </div>
      </div>

      {/* Platform Fees */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <DollarSign className="text-[#006C35]" size={24} />
          <span className="text-gray-600">Platform Fees</span>
        </div>
        <div className="text-3xl text-[#1E3A8A]">
          {stats.platformFees.toLocaleString()} SAR
        </div>
        <div className="text-sm text-gray-500 mt-2">
          From completed jobs
        </div>
      </div>
    </div>
  );
}